import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { getLocationLevelLabel, isLocationTypeEnabled } from "api-types/locations/locationDisplay";
import Flags from "common/constants/flags";
import { isUnarchiveSelfNameConflictError } from "common/types/structuredErrorResponse";
import { getBuilderLocationsURL } from "lib/endpointHelpers";
import { locationsPath, locationPublicMapPath, editLocationPath } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { deleteCommunity, putLocation } from "lib/storage/locationStorage";
import { copyTextWithNotification } from "lib/utils/clipboard";
import type { FormEvent } from "react";
import { useState } from "react";
import { useSWRConfig } from "swr";
import ConfirmDeleteModal from "ui/components/ConfirmDeleteModal";
import DropdownMenu, { DropdownMenuButton } from "ui/components/DropdownMenu";
import Link from "ui/components/Link";
import useAppContext from "ui/hooks/useAppContext";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import CommunityDetailsModal from "./CommunityDetailsModal";
import UnarchiveLocationModal from "./UnarchiveLocationModal";

type SetModalFn = (arg0: boolean) => void;

export interface CommunityDropdownModalState {
  openDetailsModal: boolean;
  setOpenDetailsModal: SetModalFn;
  openDeleteModal: boolean;
  setOpenDeleteModal: SetModalFn;
  /** Active location name blocking an unarchive; opens the rename-and-unarchive modal. */
  unarchiveConflictName: string | null;
  setUnarchiveConflictName: (name: string | null) => void;
}

interface CommunityDropdownProps {
  location: ClientLocationWithLevel;
  salesMapEnabled: boolean;
  modalState: CommunityDropdownModalState;
  onSuccess: (location: ClientLocationWithLevel) => void;
  hoverBlue?: boolean;
}

export default function CommunityDropdown({
  location,
  salesMapEnabled,
  modalState,
  hoverBlue,
  onSuccess,
}: CommunityDropdownProps) {
  const [openMenu, setOpenMenu] = useState(false);
  const { setUserNotification } = useAppContext();
  const { hasPermission, isSuperAdmin, builder } = useUserContext();
  const { mutate } = useSWRConfig();
  const locationLevel = getLocationLevelLabel(location, builder?.locationSettings);

  const handleArchiveCommunity = async (e: any): Promise<void> => {
    e.stopPropagation();

    const archivedAt = location.archivedAt ? null : new Date();
    const [updatedCommunity, err] = await putLocation(location.builderId, location.id, { archivedAt });

    if (err) {
      if (isUnarchiveSelfNameConflictError(err.data)) {
        modalState.setUnarchiveConflictName(err.data.conflictingName);
      } else {
        setUserNotification(err);
      }
    } else {
      setUserNotification({
        messageType: "success",
        message: `${locationLevel} has been ${archivedAt ? "archived" : "unarchived"}.`,
      });

      onSuccess(updatedCommunity);

      // Archiving / unarchiving cascades to descendants on the server, so invalidate any cached
      // location data for this builder (sidebar, lists, detail fetches).
      await mutate((key: string) => {
        const baseUrl = getBuilderLocationsURL({ builderId: location.builderId });
        return typeof key === "string" && key.includes(baseUrl);
      });
    }

    setOpenMenu(false);
  };

  const handleClick = () => {
    setOpenMenu(!openMenu);
  };

  const handlePublicLinkClick = async (): Promise<void> => {
    const link = Flags.absoluteUrl(locationPublicMapPath(builder?.id ?? "", location?.id));
    const copyNotification = await copyTextWithNotification(link);

    setUserNotification(copyNotification);
  };

  // Check if the location level is enabled when trying to unarchive
  const isLevelEnabled =
    location.locationLevel?.type && isLocationTypeEnabled(location.locationLevel.type, builder?.locationSettings);
  const canToggleArchiveStatus = !location.archivedAt || isLevelEnabled;
  const unarchiveDisabledTooltip = !isLevelEnabled
    ? `${locationLevel} is currently disabled in your location settings. You'll need to enable ${locationLevel} there before unarchiving this location.`
    : undefined;

  return (
    <div style={{ position: "relative" }}>
      <DropdownMenuButton open={openMenu} handleClick={handleClick} hoverBlue={hoverBlue} />
      {openMenu && (
        <DropdownMenu onClose={() => setOpenMenu(false)}>
          <Link
            disabled={!hasPermission("EDIT_COMMUNITIES", { resourceId: location.id })}
            disabledCx="disabled"
            href={editLocationPath({
              builderId: location.builderId,
              locationId: location.id,
              redirectTo: window.location.pathname,
            })}
          >
            Edit {locationLevel} Details
          </Link>
          {salesMapEnabled && (
            <Link key="public_map_link" onClick={handlePublicLinkClick}>
              Copy Sales Map Link
            </Link>
          )}
          {hasPermission("ARCHIVE_COMMUNITIES") && (
            <Link
              disabled={!canToggleArchiveStatus}
              disabledCx="disabled"
              title={unarchiveDisabledTooltip}
              onClick={handleArchiveCommunity}
            >
              {location.archivedAt ? `Unarchive ${locationLevel}` : `Archive ${locationLevel}`}
            </Link>
          )}
          {isSuperAdmin && (
            <Link
              disabled={!location.archivedAt}
              disabledCx="disabled"
              title={`${locationLevel} must be archived first to delete.`}
              onClick={(e) => {
                e.stopPropagation();
                modalState.setOpenDeleteModal(true);
                setOpenMenu(false);
              }}
            >
              Delete {locationLevel}
            </Link>
          )}
        </DropdownMenu>
      )}
    </div>
  );
}

export function useCommunityDropdownModalState(): CommunityDropdownModalState {
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [unarchiveConflictName, setUnarchiveConflictName] = useState<string | null>(null);

  return {
    openDetailsModal,
    setOpenDetailsModal,
    openDeleteModal,
    setOpenDeleteModal,
    unarchiveConflictName,
    setUnarchiveConflictName,
  };
}

interface CommunityDropdownModalsProps {
  modalState: CommunityDropdownModalState;
  onSuccess: (community: ClientLocationWithLevel) => void;
  location: ClientLocationWithLevel | null;
}

export const CommunityDropdownModals = ({ modalState, location, onSuccess }: CommunityDropdownModalsProps) => {
  const { setUserNotification } = useAppContext();
  const { builder } = useUserContext();
  const navigate = useAppNavigate();
  const { mutate } = useSWRConfig();
  const [isDeleting, setIsDeleting] = useState(false);

  if (!location) {
    return null;
  }

  const locationLevel = getLocationLevelLabel(location, builder?.locationSettings);

  const handleSubmitDelete = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsDeleting(true);

    const err = await deleteCommunity(location.builderId, location.id);

    if (err) {
      setUserNotification(err);
      setIsDeleting(false);
      return;
    }

    setIsDeleting(false);
    modalState.setOpenDeleteModal(false);
    setUserNotification({ message: `${locationLevel} deleted.`, messageType: "success" });

    // Trigger revalidation of locations data using the endpoint helper
    await mutate((key: string) => {
      const baseUrl = getBuilderLocationsURL({ builderId: location.builderId });
      return typeof key === "string" && key.includes(baseUrl);
    });

    await navigate({ to: locationsPath(location.builderId) });
  };

  const deleteMessage = `Deleting this ${locationLevel} will destroy any Lots, Plans, Home Plans within the ${locationLevel}.`;

  return (
    <>
      {modalState.openDeleteModal && (
        <ConfirmDeleteModal
          onClose={() => modalState.setOpenDeleteModal(false)}
          onConfirm={handleSubmitDelete}
          entityName={location?.name}
          entityType={locationLevel}
          isDeleting={isDeleting}
          message={deleteMessage}
        />
      )}
      {modalState.openDetailsModal && (
        <CommunityDetailsModal
          location={location}
          open
          onSuccess={onSuccess}
          onClose={() => modalState.setOpenDetailsModal(false)}
        />
      )}
      {modalState.unarchiveConflictName !== null && (
        <UnarchiveLocationModal
          location={location}
          conflictingName={modalState.unarchiveConflictName}
          onSuccess={onSuccess}
          onClose={() => modalState.setUnarchiveConflictName(null)}
        />
      )}
    </>
  );
};
